import { parseApiDate } from '../../services/dayjs';
import { PropertySearchResult } from '../../types/propertySearch';
import { capitalize } from '../text';

interface ViewingsContent {
  text: string;
  links: string[];
  screens: string[];
  linksText: string[];
  props: any[];
  date: string;
}

const getDateText = (dateStr: string | undefined) => {
  if (dateStr) {
    const date = parseApiDate(dateStr);

    if (date.isToday()) return 'Today';

    if (date.isTomorrow()) return 'Tomorrow';

    return '';
  }

  return null;
};

export const getTermParam = (result: PropertySearchResult) => {
  const term = result.params.term && result.params.term.length > 0 ? result.description.short : '';

  if (!term || term === 'Northern Ireland') {
    return '';
  }

  return term.toLowerCase().replace('derry / ', '').replace(/ /g, '-');
};

export const getShowHomeTimesUrl = (termParam: string) => {
  if (termParam) {
    return `/show-home-opening-times/${termParam}`;
  }

  return '/show-home-opening-times';
};

export const getOpeningViewsUrl = (params: PropertySearchResult['params'], date: string) => {
  let qs = '';
  if (params.saleType && params.saleType.length > 0) qs += `?saleType=${params.saleType.includes('rent') ? 2 : 1}`;
  if (params.term && params.term.length > 0) {
    qs += `${qs.length > 0 ? '&' : '?'}term=${encodeURIComponent(params.term[0])}`;
  }
  qs += `${qs.length > 0 ? '&' : '?'}date=${date.substring(0, 10)}`;
  return `/open-viewings${qs}`;
};

const getOpenViewingsContent = (searchResult?: PropertySearchResult): ViewingsContent | null => {
  if (searchResult) {
    const { nextOpenViewingTime, nextShowHomeOpenTime, description, params } = searchResult;
    const openViewingsText = getDateText(nextOpenViewingTime);
    const showHomeTimeText = getDateText(nextShowHomeOpenTime);
    const termText = params.term && params.term.length > 0 ? ` in ${capitalize(description.short)}` : '';
    const termParam = getTermParam(searchResult);
    const openViewingProps = {
      location: params.term ? params.term[0] : '3',
      saleType: params.saleType?.includes('rent') ? '2' : '1',
      date: nextOpenViewingTime,
      termParam,
    };

    // We have both open viewings and show home times
    if (openViewingsText !== null && showHomeTimeText !== null && nextOpenViewingTime) {
      if (openViewingsText === showHomeTimeText) {
        return {
          text: `View all Show Homes and Open Viewings${termText} ${openViewingsText}`,
          links: [getOpeningViewsUrl(params, nextOpenViewingTime), getShowHomeTimesUrl(termParam)],
          screens: ['OpenViewingsScreen', 'ShowHomesScreen'],
          props: [openViewingProps, { termParam }],
          linksText: ['Open Viewings', 'Show Homes'],
          date: nextOpenViewingTime,
        };
      }

      return {
        text: `View all Show Homes and Open Viewings${termText}`,
        links: [getOpeningViewsUrl(params, nextOpenViewingTime), getShowHomeTimesUrl(termParam)],
        screens: ['OpenViewingsScreen', 'ShowHomesScreen'],
        props: [openViewingProps, { termParam }],
        linksText: ['Open Viewings', 'Show Homes'],
        date: nextOpenViewingTime,
      };
    }

    // We have just open viewings
    if (openViewingsText !== null && nextOpenViewingTime) {
      return {
        text: `View all Open Viewings${termText} ${openViewingsText}`,
        links: [getOpeningViewsUrl(params, nextOpenViewingTime)],
        screens: ['OpenViewingsScreen'],
        linksText: ['Open Viewings'],
        props: [openViewingProps],
        date: nextOpenViewingTime,
      };
    }

    // We have just show home times
    if (showHomeTimeText !== null && nextShowHomeOpenTime) {
      return {
        text: `View all Show Homes${termText} ${showHomeTimeText}`,
        links: [getShowHomeTimesUrl(termParam)],
        screens: ['ShowHomesScreen'],
        linksText: ['Show Homes'],
        props: [{ termParam }],
        date: nextShowHomeOpenTime,
      };
    }
  }

  return null;
};

export default getOpenViewingsContent;
